import { useState, useMemo, useCallback, useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { get as _get, orderBy as _orderBy } from 'lodash';

import { purchaseInvoicesTableCells } from 'configs';

import useDispatch from 'shared/hooks/useDispatch';
import useSelector from 'shared/hooks/useSelector';
import useCooperativeId from 'shared/hooks/useCooperativeId';
import useSort from 'shared/hooks/useSort';

import { fiscalYearsApi } from 'services/fiscal-years-api';
import { bankAccountsApi } from 'features/bank-accounts/api';
import { purchaseInvoicesApi } from 'features/purchase-invoices/api';
import {
  selectStartDate,
  selectBankAccount,
  selectEndDate,
} from 'features/purchase-invoices/selectors';
import { setEndDate, setStartDate } from 'features/purchase-invoices/slice';
import { GetRequestParams } from 'features/purchase-invoices/types';

import { filterAllBySearchTerm, orderByType, customFormat } from 'utils';

import { findLastModifiedMainBankAccount } from './helpers';

import { PurchaseInvoicesDetails } from 'components';

const PurchaseInvoices = () => {
  const dispatch = useDispatch();
  const { selectedBankAccount, selectedEndDate, selectedStartDate } =
    useSelector((state) => ({
      selectedStartDate: selectStartDate(state),
      selectedEndDate: selectEndDate(state),
      selectedBankAccount: selectBankAccount(state),
    }));
  const cooperativeId = useCooperativeId();

  const [sortParams, changeSortParams] = useSort({
    order: 'desc',
    orderBy: 'AccountingDate',
    type: 'date',
  });

  const [searchTerm, setSearchTerm] = useState('');

  const { isLoading: isLoadingFiscalYears, isError: isFiscalYearsQueryError } =
    fiscalYearsApi.useGetFiscalYearsQuery(cooperativeId ?? skipToken, {
      refetchOnMountOrArgChange: true,
    });
  const {
    data: bankAccounts,
    isLoading: isLoadingBankAccounts,
    isFetching: isFetchingBankAccounts,
    isError: isBankAccountsQueryError,
  } = bankAccountsApi.useGetDefaultPortalBankAccountsListQuery(
    cooperativeId ?? skipToken,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const selectedBankAccountsIds = useMemo(() => {
    if (selectedBankAccount.Id === 'All') {
      return (bankAccounts ?? []).map(({ Id }) => Id);
    }

    return [selectedBankAccount.Id];
  }, [bankAccounts, selectedBankAccount]);

  const requestParams = useMemo(() => {
    if (
      selectedStartDate &&
      selectedEndDate &&
      cooperativeId &&
      selectedBankAccountsIds.length
    ) {
      return {
        coopId: cooperativeId,
        endDate: customFormat(selectedEndDate, 'server'),
        startDate: customFormat(selectedStartDate, 'server'),
        selectedBankAccountsIds: selectedBankAccountsIds,
      } as GetRequestParams;
    }
    return skipToken;
  }, [
    cooperativeId,
    selectedStartDate,
    selectedEndDate,
    selectedBankAccountsIds,
  ]);

  const {
    data: summaryInfo,
    isLoading: isLoadingSummaryInfo,
    isFetching: isFetchingSummaryInfo,
    isError: isSummaryInfoQueryError,
  } = purchaseInvoicesApi.useGetSummaryInfoQuery(requestParams, {
    refetchOnMountOrArgChange: true,
  });
  const {
    data: purchaseInvoicesList,
    isLoading: isLoadingPurchaseInvoicesList,
    isFetching: isFetchingPurchaseInvoicesList,
    isError: isPurchaseInvoicesListQueryError,
  } = purchaseInvoicesApi.useGetPurchaseInvoicesListQuery(requestParams, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    return () => {
      dispatch(purchaseInvoicesApi.util.resetApiState());
      dispatch(bankAccountsApi.util.resetApiState());
    };
  }, []);

  const handleChangeDate = useCallback(
    (date: Date | null, name?: string) => {
      let setDate = name === 'startDate' ? setStartDate : setEndDate;

      dispatch(setDate(date && date.getTime()));
    },
    [dispatch]
  );

  const handleChangeSearchTerm = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      setSearchTerm(value);
    },
    []
  );

  const filteredPurchaseInvoices = useMemo(() => {
    if (!purchaseInvoicesList) return [];
    return purchaseInvoicesList.filter((purchaseInvoice) =>
      filterAllBySearchTerm(
        purchaseInvoice,
        searchTerm,
        purchaseInvoicesTableCells
      )
    );
  }, [searchTerm, purchaseInvoicesList]);

  const sortedPurchaseInvoices = useMemo(() => {
    return _orderBy(
      filteredPurchaseInvoices,
      (invoice) =>
        orderByType(_get(invoice, sortParams.orderBy), sortParams.type),
      sortParams.order || 'asc'
    );
  }, [filteredPurchaseInvoices, sortParams]);

  const displayedBankAccount = useMemo(() => {
    if (bankAccounts && selectedBankAccount.Id === 'All') {
      return findLastModifiedMainBankAccount(bankAccounts);
    } else {
      return selectedBankAccount;
    }
  }, [selectedBankAccount, bankAccounts]);

  const purchaseInvoicesListLoading =
    isLoadingPurchaseInvoicesList || isFetchingPurchaseInvoicesList;
  const summaryInfoLoading = isLoadingSummaryInfo || isFetchingSummaryInfo;
  const loading =
    isLoadingFiscalYears ||
    isLoadingBankAccounts ||
    isFetchingBankAccounts ||
    summaryInfoLoading ||
    purchaseInvoicesListLoading;
  const isError =
    isFiscalYearsQueryError ||
    isBankAccountsQueryError ||
    isSummaryInfoQueryError ||
    isPurchaseInvoicesListQueryError;

  return (
    <PurchaseInvoicesDetails
      endDate={selectedEndDate}
      error={isError}
      isLoadingPurchaseInvoicesList={purchaseInvoicesListLoading}
      loading={loading}
      displayedBankAccount={displayedBankAccount}
      purchaseInvoices={sortedPurchaseInvoices}
      searchTerm={searchTerm}
      sortParams={sortParams}
      startDate={selectedStartDate}
      summary={summaryInfo ?? null}
      handleDateChange={handleChangeDate}
      handleChangeSearchTerm={handleChangeSearchTerm}
      handleChangeSortParams={changeSortParams}
    />
  );
};

export default PurchaseInvoices;
